import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import {
  getJobRoles,
  createJobRole,
  updateJobRole,
  deleteJobRole,
  getOrganizations,
} from '../../utils/api';
import {
  Plus,
  Edit2,
  Trash2,
  Briefcase,
  X,
  Search,
} from 'lucide-react';

const JobRoles = () => {
  const [jobRoles, setJobRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    organization_id: '',
    title: '',
    description: '',
    ideal_disc_profile: '',
    ideal_d_min: 0,
    ideal_d_max: 100,
    ideal_i_min: 0,
    ideal_i_max: 100,
    ideal_s_min: 0,
    ideal_s_max: 100,
    ideal_c_min: 0,
    ideal_c_max: 100,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rolesRes, orgsRes] = await Promise.all([
        getJobRoles(),
        getOrganizations(),
      ]);
      setJobRoles(rolesRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        organization_id: role.organization_id || '',
        title: role.title,
        description: role.description || '',
        ideal_disc_profile: role.ideal_disc_profile || '',
        ideal_d_min: role.ideal_d_min || 0,
        ideal_d_max: role.ideal_d_max || 100,
        ideal_i_min: role.ideal_i_min || 0,
        ideal_i_max: role.ideal_i_max || 100,
        ideal_s_min: role.ideal_s_min || 0,
        ideal_s_max: role.ideal_s_max || 100,
        ideal_c_min: role.ideal_c_min || 0,
        ideal_c_max: role.ideal_c_max || 100,
      });
    } else {
      setEditingRole(null);
      setFormData({
        organization_id: '',
        title: '',
        description: '',
        ideal_disc_profile: '',
        ideal_d_min: 0,
        ideal_d_max: 100,
        ideal_i_min: 0,
        ideal_i_max: 100,
        ideal_s_min: 0,
        ideal_s_max: 100,
        ideal_c_min: 0,
        ideal_c_max: 100,
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingRole) {
        await updateJobRole(editingRole.id, formData);
      } else {
        await createJobRole(formData);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save job role');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job role?')) {
      return;
    }

    try {
      await deleteJobRole(id);
      loadData();
    } catch (error) {
      console.error('Error deleting job role:', error);
    }
  };

  const filteredRoles = jobRoles.filter(
    (role) =>
      role.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Roles</h1>
          <p className="text-gray-600">Define ideal DISC profiles for positions</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 bg-tsvmap-orange text-white rounded-lg hover:bg-tsvmap-orange-dark transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Job Role
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search job roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
          />
        </div>
      </div>

      {/* Job Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredRoles.length > 0 ? (
          filteredRoles.map((role) => (
            <div key={role.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="bg-tsvmap-blue/10 p-2 rounded-lg">
                    <Briefcase className="h-6 w-6 text-tsvmap-blue" />
                  </div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900">{role.title}</h3>
                    <p className="text-sm text-gray-500">
                      {role.organization_name || 'No organization'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleOpenModal(role)}
                    className="p-1 text-gray-400 hover:text-tsvmap-blue"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(role.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {role.description && (
                <p className="text-sm text-gray-600 mb-4">{role.description}</p>
              )}

              {role.ideal_disc_profile && (
                <div className="mb-4">
                  <span className="text-xs text-gray-500">Ideal Profile:</span>
                  <span className="ml-2 px-2 py-1 bg-tsvmap-orange/10 text-tsvmap-orange-dark rounded text-sm font-medium">
                    {role.ideal_disc_profile}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-4 gap-2 text-xs">
                {['D', 'I', 'S', 'C'].map((style) => {
                  const min = role[`ideal_${style.toLowerCase()}_min`];
                  const max = role[`ideal_${style.toLowerCase()}_max`];
                  const colors = {
                    D: 'bg-red-100 text-red-800',
                    I: 'bg-orange-100 text-orange-800',
                    S: 'bg-blue-100 text-blue-800',
                    C: 'bg-yellow-100 text-yellow-800',
                  };
                  return (
                    <div key={style} className={`${colors[style]} rounded p-2 text-center`}>
                      <div className="font-bold">{style}</div>
                      <div>{min}-{max}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No job roles found</p>
            <p className="text-sm mt-1">Create job roles to define ideal DISC profiles</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingRole ? 'Edit Job Role' : 'Add Job Role'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization *
                  </label>
                  <select
                    required
                    value={formData.organization_id}
                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  >
                    <option value="">Select organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                    placeholder="e.g., Sales Manager"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ideal DISC Profile (e.g., D/I, C/S)
                  </label>
                  <input
                    type="text"
                    value={formData.ideal_disc_profile}
                    onChange={(e) => setFormData({ ...formData, ideal_disc_profile: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tsvmap-orange focus:border-transparent"
                    placeholder="e.g., D/I"
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Ideal Score Ranges</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'D', label: 'Dominance', color: 'red' },
                      { key: 'I', label: 'Influence', color: 'orange' },
                      { key: 'S', label: 'Steadiness', color: 'blue' },
                      { key: 'C', label: 'Conscientiousness', color: 'yellow' },
                    ].map(({ key, label, color }) => (
                      <div key={key} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-3`}>
                        <label className={`block text-sm font-medium text-${color}-800 mb-2`}>
                          {label} ({key})
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData[`ideal_${key.toLowerCase()}_min`]}
                            onChange={(e) => setFormData({
                              ...formData,
                              [`ideal_${key.toLowerCase()}_min`]: parseInt(e.target.value) || 0
                            })}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={formData[`ideal_${key.toLowerCase()}_max`]}
                            onChange={(e) => setFormData({
                              ...formData,
                              [`ideal_${key.toLowerCase()}_max`]: parseInt(e.target.value) || 100
                            })}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-tsvmap-orange text-white rounded-lg hover:bg-tsvmap-orange-dark"
                  >
                    {editingRole ? 'Save Changes' : 'Add Job Role'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default JobRoles;
